const PERSISTENT_CODES = new Set([
  'guest_daily_limit', 'daily_limit_reached', 'guest_pending_upload_limit',
  'guest_session_expired', 'guest_session_ended', 'guest_signup_rate_limit',
  'upload_pending_limit', 'upload_id_conflict', 'upload_already_active',
]);

export const UPLOAD_ERROR_CODES = new Set([
  ...PERSISTENT_CODES, 'upload_invalid_filename', 'upload_invalid_size', 'file_too_large',
  'upload_invalid_id', 'upload_invalid_credential', 'upload_session_expired', 'upload_cancelled',
  'upload_storage_full', 'upload_not_found', 'upload_busy', 'upload_offset_conflict',
  'upload_invalid_chunk', 'upload_incomplete', 'upload_invalid_metadata', 'upload_invalid_offset',
  'upload_request_failed', 'upload_timeout', 'upload_stalled', 'upload_connection_interrupted',
  'upload_chunk_interrupted', 'auth_verification_unavailable', 'unsupported_format', 'upload_missing_source',
]);

export const isPersistentUploadFailure = error => PERSISTENT_CODES.has(error?.code || error?.payload?.code);

export function normalizeUploadFailure(payload = {}, status) {
  payload = payload || {};
  if (payload.error && typeof payload.error === 'object') payload = { ...payload,
    code: payload.code || payload.error.code, error: payload.error.message || 'The file could not be prepared.' };
  if (!payload.code && UPLOAD_ERROR_CODES.has(payload.error_code)) payload = { ...payload, code: payload.error_code };
  // Older API releases used a plain 400 response for the registered allowance.
  if (!payload.code && status === 400 && typeof payload.error === 'string'
      && /^Free users are allowed a maximum of 3 transcriptions per day\.?$/i.test(payload.error.trim())) {
    return { ...payload, code: 'daily_limit_reached' };
  }
  return payload;
}

export function quotaResetLabel(quota, language = 'en') {
  const date = new Date(quota?.reset_at);
  if (!Number.isFinite(date.getTime())) return '';
  try {
    return new Intl.DateTimeFormat(language, { dateStyle: 'medium', timeStyle: 'short',
      timeZone: quota?.timezone || 'Europe/Berlin' }).format(date);
  } catch { return ''; }
}

export function uploadRecovery(payload = {}, status) {
  payload = normalizeUploadFailure(payload, status);
  const code = payload.code;
  if (code === 'upload_already_active') return {
    code, kind: 'active_upload', messageKey: 'upload.alreadyActive', action: 'wait',
  };
  if (code==='auth_verification_unavailable') return {
    code,kind:'auth_temporary',messageKey:'upload.authUnavailable',action:'wait',
  };
  if (['guest_daily_limit', 'daily_limit_reached'].includes(code)) {
    const values = payload.quota || payload;
    const quota = Number.isFinite(values.remaining)
      ? Object.fromEntries(['used','remaining','limit','reset_at','retry_after','timezone'].map(key => [key, values[key]])) : undefined;
    return { code, kind: 'daily_limit', messageKey: 'upload.dailyLimit', action: 'signin', quota,
      ...(payload.id ? { recordId: payload.id } : {}) };
  }
  if (code === 'guest_pending_upload_limit') {
    return { code, kind: 'pending_limit', messageKey: 'upload.pendingLimit', action: 'history' };
  }
  if (code === 'upload_pending_limit') {
    return { code, kind: 'pending_sessions', messageKey: 'upload.pendingSessions', action: 'wait' };
  }
  if (['upload_session_expired', 'upload_cancelled', 'upload_not_found'].includes(code)) {
    return { code, kind: 'upload_session', messageKey: 'upload.selectAgain', action: 'wait' };
  }
  if (['guest_session_expired', 'guest_session_ended'].includes(code) || status === 401) {
    return { code: ['guest_session_expired', 'guest_session_ended'].includes(code) ? code : 'session_not_found',
      kind: 'session', messageKey: 'upload.sessionExpired', action: 'signin' };
  }
  if (code === 'guest_signup_rate_limit' || status === 429) {
    return { code: code === 'guest_signup_rate_limit' ? code : 'upload_rate_limited',
      kind: 'rate_limit', messageKey: 'upload.rateLimited', action: 'wait' };
  }
  return null;
}

export function uploadErrorCode(payload = {}, status) {
  payload = normalizeUploadFailure(payload, status);
  if (UPLOAD_ERROR_CODES.has(payload.code)) return payload.code;
  if (status === 413) return 'file_too_large';
  if (status === 429) return 'upload_rate_limited';
  if (status >= 400 && status <= 599) return `http_${status}`;
  return 'upload_connection_interrupted';
}
